import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { ApplicationType, FlightZoneListFilters, FlightZonesManagementListItem } from "@dtm-frontend/dss-shared-lib";
import { PAGE_NUMBER_QUERY_PARAM, PAGE_SIZE_QUERY_PARAM } from "@dtm-frontend/shared/ui";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngxs/store";
import { distinctUntilChanged } from "rxjs";
import { FlightZonesManagementTableColumns, TableActionButton } from "../../models/flight-zone-management.models";
import { ApplicationKmlExportService } from "../../services/application-kml-export.service";
import { FlightZoneListsActions } from "../../state/flight-zone-lists.actions";
import { FlightZoneListsState } from "../../state/flight-zone-lists.state";

@UntilDestroy()
@Component({
    selector: "dss-admin-lib-rejected-applications-list-container",
    templateUrl: "./rejected-applications-list-container.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RejectedApplicationsListContainerComponent implements OnInit {
    protected readonly displayedColumns: FlightZonesManagementTableColumns = [
        "expandHandle",
        "date",
        "startAt",
        "endAt",
        "applicationPurpose",
        "title",
        "actions",
    ];
    protected readonly initialFilters: FlightZoneListFilters;
    protected readonly isProcessing$ = this.store.select(FlightZoneListsState.isProcessing);
    protected readonly rejectedApplicationsList$ = this.store.select(FlightZoneListsState.rejectedApplicationsList);
    protected readonly hasListDataRetrievalError$ = this.store.select(FlightZoneListsState.hasListDataRetrievalError);
    protected readonly listFiltersCapabilities$ = this.store.select(FlightZoneListsState.listFiltersCapabilities);
    protected readonly institutionsFilterOptions$ = this.store.select(FlightZoneListsState.institutionsFilterOptions);
    protected readonly actionButtons = [TableActionButton.PreviewAction, TableActionButton.KmlDownload];

    constructor(
        private readonly store: Store,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly applicationKmlExportService: ApplicationKmlExportService
    ) {
        this.initialFilters = this.route.snapshot.queryParams;
    }

    public ngOnInit(): void {
        this.updateListOnQueryParamsChange();
        this.store.dispatch(new FlightZoneListsActions.GetInstitutionsFilterOptions());
    }

    protected applyFilters(filters: FlightZoneListFilters): void {
        this.updateQueryParams(filters);
    }

    protected findInstitutionByText(searchValue?: string): void {
        this.store.dispatch(new FlightZoneListsActions.GetInstitutionsFilterOptions(searchValue));
    }

    protected clearFilterLists(): void {
        this.store.dispatch(new FlightZoneListsActions.ClearFilterLists());
    }

    protected changePage(event: PageEvent): void {
        const queryParams = { [PAGE_NUMBER_QUERY_PARAM]: event.pageIndex, [PAGE_SIZE_QUERY_PARAM]: event.pageSize };

        this.updateQueryParams(queryParams);
    }

    protected refreshList(): void {
        this.store.dispatch(new FlightZoneListsActions.GetRejectedApplicationsList(this.route.snapshot.queryParams));
    }

    protected downloadKml(flightZone: FlightZonesManagementListItem): void {
        this.applicationKmlExportService.downloadKml(flightZone, ApplicationType.RestrictionApplication);
    }

    private updateQueryParams(queryParams: Params): void {
        this.router.navigate(["."], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge",
        });
    }

    private updateListOnQueryParamsChange(): void {
        this.route.queryParams.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((filterParams) => {
            this.store.dispatch(new FlightZoneListsActions.GetRejectedApplicationsList(filterParams));
        });
    }
}
